import { throttle } from "lodash-es";

export default class MaestroDirectiveProxy {
  static COMMA_SPLIT_REGEX = /(?:[\s]+)?,(?:[\s]+)?/;

  static DOM_TO_CUSTOM_EVENT_MAP = {
    mouseenter: "hover",
  };

  static CUSTOM_TO_DOM_EVENT_MAP = {
    hover: {
      domEvent: "mouseenter",
      isCancelable: {
        domEvent: "mouseleave",
      },
    },
  };

  constructor() {
    this.events = new Map();
    const elements = document.querySelectorAll("[data-mtr-track]");
    elements.forEach((el) => {
      this.addMaestroTrackEvent(el);
    });

    const mutationHandler = (mutationList, observer) => {
      mutationList.forEach((mutationRecord) => {
        if (mutationRecord.type === "childList") {
          const addedNodes = [];
          const removedNodes = [];
          mutationRecord.addedNodes.forEach(
            (node) => node.nodeType === 1 && addedNodes.push(node)
          );
          mutationRecord.removedNodes.forEach(
            (node) => node.nodeType === 1 && removedNodes.push(node)
          );

          addedNodes.forEach((addedEl) => {
            if (addedEl.dataset.mtrTrack) {
              this.addMaestroTrackEvent(addedEl);
            }

            addedEl
              .querySelectorAll("[data-mtr-track]")
              .forEach((addedChildEl) => {
                this.addMaestroTrackEvent(addedChildEl);
              });
          });

          removedNodes.forEach((removedEl) => {
            if (removedEl.dataset.mtrTrack) {
              this.removeMaestroTrackEvent(removedEl);
            }

            removedEl
              .querySelectorAll("[data-mtr-track]")
              .forEach((removedChildEl) => {
                this.removeMaestroTrackEvent(removedChildEl);
              });
          });
        }

        if (mutationRecord.type === "attributes") {
          const targetEl = mutationRecord.target;
          if (targetEl.dataset.mtrTrack) {
            this.addMaestroTrackEvent(targetEl);
          }
        }
      });
    };

    const config = { attributes: true, childList: true, subtree: true };
    this.observer = new MutationObserver(mutationHandler);
    this.observer.observe(document, config);
  }

  parseDOMEventDetails(mtrTrack, mtrAs = null) {
    const [name, delay] = mtrTrack.split(":");

    return {
      as: mtrAs,
      name,
      delay: delay ? parseFloat(delay) : undefined,
    };
  }

  addMaestroTrackEvent(domElement) {
    if (this.events.get(domElement)) {
      return;
    }

    this.events.set(domElement, new Map());

    const trackEvents = domElement.dataset.mtrTrack.split(
      MaestroDirectiveProxy.COMMA_SPLIT_REGEX
    );
    const asEvents =
      domElement.dataset.mtrAs?.split?.(
        MaestroDirectiveProxy.COMMA_SPLIT_REGEX
      ) ?? [];

    const eventMap = this.events.get(domElement);

    trackEvents.forEach((mtrTrack, idx) => {
      const { as, name, delay } = this.parseDOMEventDetails(
        mtrTrack,
        asEvents[idx]
      );
      const handler = delay
        ? throttle(this.fireMaestroTrackEvent, delay, { leading: false })
        : this.fireMaestroTrackEvent;

      eventMap.set(name, {
        as,
        name,
        delay,
        handler,
      });

      const customEvent = MaestroDirectiveProxy.CUSTOM_TO_DOM_EVENT_MAP[name];

      if (customEvent) {
        domElement.addEventListener(customEvent.domEvent, handler);

        if (delay && customEvent.isCancelable) {
          domElement.addEventListener(
            customEvent.isCancelable.domEvent,
            handler.cancel
          );
        }
      } else {
        domElement.addEventListener(name, handler);
      }
    });
  }

  removeMaestroTrackEvent(domElement) {
    const eventMap = this.events.get(domElement);

    if (!eventMap) {
      return;
    }

    const trackEvents = Array.from(eventMap.keys() ?? []);

    trackEvents.forEach((mtrTrack) => {
      const { name, delay } = this.parseDOMEventDetails(mtrTrack);
      const { handler } = eventMap.get(name);

      const customEvent = MaestroDirectiveProxy.CUSTOM_TO_DOM_EVENT_MAP[name];

      if (customEvent) {
        domElement.removeEventListener(customEvent.domEvent, handler);

        if (delay && customEvent.isCancelable) {
          domElement.removeEventListener(
            customEvent.isCancelable.domEvent,
            handler.cancel
          );
        }
      } else {
        domElement.removeEventListener(name, handler);
      }
    });

    this.events.delete(domElement);
  }

  fireMaestroTrackEvent = (domEvent) => {
    const currentEl = domEvent.currentTarget;
    const targetEl = domEvent.target;
    const throttledEl = currentEl ?? targetEl;

    const domEventTarget =
      this.events.get(currentEl) ?? this.events.get(targetEl);
    const domEventType =
      MaestroDirectiveProxy.DOM_TO_CUSTOM_EVENT_MAP[domEvent.type] ??
      domEvent.type;
    const analyticsEvent = domEventTarget?.get(domEventType)?.as;
    const rawData = currentEl?.dataset?.mtrData;

    try {
      let data = {};

      if (rawData) {
        try {
          data = JSON.parse(rawData.trim());
        } catch (e) {
          console.error("Could not parse [data-mtr-data] value");
        }
      } else {
        // This value provides Maestro with additional element context
        // In order:
        const eventContext =
          // - default to the value of the throttled element's [data-mtr-target] attribute
          //   (this enables custom text in pagination arrows, for example)
          (
            throttledEl?.dataset?.mtrLabel ??
            // If no [data-mtr-label] attribute is found
            // - attempt to read target element's [data-mtr-label] attribute
            targetEl?.dataset?.mtrLabel ??
            // - If no [data-mtr-label] is found, attempt to read the target element's current source
            targetEl?.currentSrc ??
            // - If no current source is found, attempt to read the target element's source
            targetEl?.src ??
            // - If all else fails, assume a text node and read the elements text contents
            targetEl?.textContent
          )?.trim() ?? null;

        // Provides Maestro with the event target
        // In order:
        const eventTarget =
          // - default to the value of the throttled element's [data-mtr-target] attribute
          throttledEl?.dataset?.mtrTarget ??
          // If no [data-mtr-target] attribute is found
          // - attempt to read target element's [data-mtr-target] attribute
          targetEl?.dataset?.mtrTarget ??
          // - If no [data-mtr-target] attribute is found,
          //   attempt to read the target element's [data-tbsp-component] attribute
          targetEl?.dataset.tbspComponent ??
          // - If all else fails, we grab the target element's node name (lowercased)
          targetEl?.nodeName?.toLowerCase() ??
          null;

        // Find the nearest [data-mtr-section] ancestor, and use this as the page section value
        const eventPageSection =
          targetEl?.closest("[data-mtr-section]")?.dataset?.mtrSection ?? null;

        // Find the nearest [data-mtr-position] ancestor, and use this as the position value
        const eventPosition =
          throttledEl?.closest("[data-mtr-position]")?.dataset?.mtrPosition ??
          null;

        const currentUrl = throttledEl?.href ? new URL(throttledEl.href) : null;

        const destinationUrl = currentUrl?.toString() ?? null;
        const destinationPath = currentUrl
          ? `${currentUrl.pathname}${currentUrl.search ?? ""}${
              currentUrl.hash ?? ""
            }`
          : null;

        const fromUrl = window.location.href;
        const fromPath = `${window.location.pathname}${
          window.location.search ?? ""
        }${window.location.hash ?? ""}`;

        let canonicalData = {};
        const canonicalHref = throttledEl?.dataset?.mtrCanonicalHref;

        if (canonicalHref) {
          let constructedUrl = null;

          if (canonicalHref.startsWith("http")) {
            constructedUrl = new URL(canonicalHref);
          } else if (canonicalHref.startsWith("//")) {
            constructedUrl = new URL(`${location.protocol}${canonicalHref}`);
          } else {
            constructedUrl = new URL(
              `${window.origin}${
                canonicalHref.startsWith("/")
                  ? canonicalHref
                  : `/${canonicalHref}`
              }`
            );
          }

          const canonicalUrl = constructedUrl.toString();

          canonicalData = {
            "canonical url": canonicalUrl,
            "canonical path": constructedUrl.pathname,
          };
        }

        // If product-related, find the nearest [data-mtr-product-*] ancestor,
        // and use this as product metadata
        let productMeta = {};

        const productName = throttledEl?.closest("[data-mtr-product-name]")
          ?.dataset?.mtrProductName;

        if (productName) {
          productMeta = {
            "product name": productName,
          };

          const productId = throttledEl?.closest("[data-mtr-product-id]")
            ?.dataset?.mtrProductId;

          if (productId) {
            productMeta = {
              ...productMeta,
              "product id": productId,
            };
          }

          const productCategory = throttledEl?.closest(
            "[data-mtr-product-category]"
          )?.dataset?.mtrProductCategory;

          if (productCategory) {
            productMeta = {
              ...productMeta,
              "product category": productCategory,
            };
          }

          const productPrice = throttledEl?.closest("[data-mtr-product-price]")
            ?.dataset?.mtrProductPrice;

          if (productPrice) {
            productMeta = {
              ...productMeta,
              "product price": productPrice,
            };
          }

          const variantName = throttledEl?.closest(
            "[data-mtr-product-variant-name]"
          )?.dataset?.mtrProductVariantName;

          if (variantName) {
            productMeta = {
              ...productMeta,
              "product color": variantName,
              "variant name": variantName,
            };
          }

          const variantId = throttledEl?.closest(
            "[data-mtr-product-variant-id]"
          )?.dataset?.mtrProductVariantId;

          if (variantId) {
            productMeta = {
              ...productMeta,
              "variant id": variantId,
            };
          }

          const variantPrice = throttledEl?.closest(
            "[data-mtr-product-variant-price]"
          )?.dataset?.mtrProductVariantPrice;

          if (variantPrice) {
            productMeta = {
              ...productMeta,
              "variant price": variantPrice,
            };
          }

          const variantSku = throttledEl?.closest(
            "[data-mtr-product-variant-sku]"
          )?.dataset?.mtrProductVariantSku;

          if (variantSku) {
            productMeta = {
              ...productMeta,
              "variant sku": variantSku,
            };
          }
        }

        const level =
          throttledEl?.closest("[data-mtr-level]")?.dataset?.mtrLevel ?? null;

        const levelHeader =
          throttledEl?.closest("[data-mtr-level-header]")?.dataset
            ?.mtrLevelHeader ?? null;

        // Build the data object to pass to Maestro
        data = {
          ...canonicalData,
          "dest url": destinationUrl,
          "dest path": destinationPath,
          "from url": fromUrl,
          "from path": fromPath,
          data: eventContext,
          target: eventTarget,
          "page section": eventPageSection,
          position: eventPosition,
        };

        data = {
          ...data,
          ...productMeta,
        };

        if (level || levelHeader) {
          data = {
            ...data,
            level,
            "level header": levelHeader,
          };
        }
      }

      const maestroEvent = new CustomEvent("mtr:track", {
        detail: {
          analyticsEvent,
          data,
          domEvent,
        },
      });
      document.dispatchEvent(maestroEvent);
    } catch (e) {
      console.error("Could not parse [data-mtr-data] value", e);
    }
  };
}
